import { apiClient } from './api';

/**
 * Admin API Client
 * Handles all admin-related API calls
 */

// ============================================================================
// TYPES
// ============================================================================

export interface AdminLoginRequest {
  username: string;
  password: string;
  mfaToken?: string;
}

export interface AdminLoginResponse {
  success: true;
  message: string;
  data: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    tokenType: string;
  };
}

export interface AdminMFARequiredResponse {
  requiresMfa: true;
  message: string;
  adminId: string;
}

export interface AdminInfo {
  id: string;
  username: string;
  email: string;
  role: 'SUPER_ADMIN' | 'ELECTION_MANAGER' | 'AUDITOR';
  permissions: Record<string, boolean>;
  status: 'ACTIVE' | 'SUSPENDED' | 'REVOKED';
  mfaEnabled: boolean;
  createdAt: string;
  lastLogin: string | null;
}

export interface CreateAdminRequest {
  username: string;
  email: string;
  password: string;
  role: 'SUPER_ADMIN' | 'ELECTION_MANAGER' | 'AUDITOR';
  permissions?: Record<string, boolean>;
}

export interface VoterListItem {
  id: string;
  member_id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  registration_timestamp: string;
  last_login: string | null;
  mfa_enabled: boolean;
  created_at: string;
}

export interface VoterStatistics {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  inactive: number;
}

export interface ElectionCreateRequest {
  title: string;
  description: string;
  start_timestamp: string;
  end_timestamp: string;
  eligibility_criteria: Record<string, unknown>;
  ballot_races: Array<{
    title: string;
    description?: string;
    race_type: 'SINGLE_CHOICE' | 'MULTI_CHOICE';
    max_selections: number;
    display_order: number;
    options: Array<{
      option_text: string;
      option_order: number;
    }>;
  }>;
}

export interface Election {
  id: string;
  title: string;
  description: string;
  start_timestamp: string;
  end_timestamp: string;
  status: 'UPCOMING' | 'ACTIVE' | 'CLOSING' | 'CLOSED';
  eligibility_criteria: Record<string, unknown>;
  total_votes_cast: number;
  created_at: string;
}

// ============================================================================
// ADMIN AUTHENTICATION API
// ============================================================================

const ADMIN_ACCESS_TOKEN_KEY = 'admin_access_token';
const ADMIN_REFRESH_TOKEN_KEY = 'admin_refresh_token';

export const adminAuthApi = {
  /**
   * Admin login
   */
  login: async (
    data: AdminLoginRequest
  ): Promise<AdminLoginResponse | AdminMFARequiredResponse> => {
    const response = await apiClient.post<AdminLoginResponse | AdminMFARequiredResponse>(
      '/admin/auth/login',
      data
    );
    return response;
  },

  /**
   * Refresh admin access token
   */
  refresh: async (refreshToken: string) => {
    const response = await apiClient.post<{
      success: boolean;
      data: {
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
        tokenType: string;
      };
    }>('/admin/auth/refresh', { refreshToken });
    return response.data;
  },

  /**
   * Get current admin info
   */
  getMe: async (): Promise<AdminInfo> => {
    const response = await apiClient.get<{
      success: boolean;
      data: AdminInfo;
    }>('/admin/auth/me');
    return response.data;
  },

  /**
   * Admin logout
   */
  logout: async () => {
    return apiClient.post('/admin/auth/logout');
  },

  /**
   * Setup MFA
   */
  setupMFA: async (password: string) => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: {
        secret: string;
        qrCode: string;
      };
    }>('/admin/auth/mfa/setup', { password });
    return response.data;
  },

  /**
   * Verify and enable MFA
   */
  verifyMFA: async (token: string, secret: string) => {
    return apiClient.post('/admin/auth/mfa/verify', { token, secret });
  },

  /**
   * Disable MFA
   */
  disableMFA: async (password: string, token: string) => {
    return apiClient.post('/admin/auth/mfa/disable', { password, token });
  },

  /**
   * Create new admin (SUPER_ADMIN only)
   */
  createAdmin: async (data: CreateAdminRequest) => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: {
        adminId: string;
        username: string;
        email: string;
      };
    }>('/admin/users', data);
    return response.data;
  },

  // Token management helpers
  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem(ADMIN_ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(ADMIN_REFRESH_TOKEN_KEY, refreshToken);
    // Also set in main API client
    apiClient.setTokens(accessToken, refreshToken);
  },

  getAccessToken: () => {
    return localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  },

  getRefreshToken: () => {
    return localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY);
  },

  clearTokens: () => {
    localStorage.removeItem(ADMIN_ACCESS_TOKEN_KEY);
    localStorage.removeItem(ADMIN_REFRESH_TOKEN_KEY);
    apiClient.clearTokens();
  },

  isAuthenticated: () => {
    return !!localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
  },
};

// ============================================================================
// VOTER MANAGEMENT API
// ============================================================================

export const adminVoterApi = {
  /**
   * List all voters with filters
   */
  listVoters: async (params?: {
    status?: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ voters: VoterListItem[]; total: number }> => {
    const response = await apiClient.get<{
      success: boolean;
      data: VoterListItem[];
      meta: {
        total: number;
        limit: number;
        offset: number;
      };
    }>('/admin/voters', { params });
    return {
      voters: response.data,
      total: response.meta.total,
    };
  },

  /**
   * Get pending voters
   */
  getPendingVoters: async (): Promise<VoterListItem[]> => {
    const response = await apiClient.get<{
      success: boolean;
      data: VoterListItem[];
      meta: {
        count: number;
      };
    }>('/admin/voters/pending');
    return response.data;
  },

  /**
   * Get voter statistics
   */
  getStatistics: async (): Promise<VoterStatistics> => {
    const response = await apiClient.get<{
      success: boolean;
      data: VoterStatistics;
    }>('/admin/voters/statistics');
    return response.data;
  },

  /**
   * Approve voter
   */
  approveVoter: async (voterId: string, reason?: string) => {
    return apiClient.post(`/admin/voters/${voterId}/approve`, { reason });
  },

  /**
   * Reject voter
   */
  rejectVoter: async (voterId: string, reason: string) => {
    return apiClient.post(`/admin/voters/${voterId}/reject`, { reason });
  },

  /**
   * Suspend voter
   */
  suspendVoter: async (voterId: string, reason: string) => {
    return apiClient.post(`/admin/voters/${voterId}/suspend`, { reason });
  },

  /**
   * Reactivate voter
   */
  reactivateVoter: async (voterId: string, reason?: string) => {
    return apiClient.post(`/admin/voters/${voterId}/reactivate`, { reason });
  },
};

// ============================================================================
// ELECTION MANAGEMENT API
// ============================================================================

export const adminElectionApi = {
  /**
   * Create election
   */
  createElection: async (data: ElectionCreateRequest): Promise<Election> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: Election;
    }>('/elections', data);
    return response.data;
  },

  /**
   * Update election
   */
  updateElection: async (
    electionId: string,
    data: Partial<ElectionCreateRequest>
  ): Promise<Election> => {
    const response = await apiClient.patch<{
      success: boolean;
      message: string;
      data: Election;
    }>(`/elections/${electionId}`, data);
    return response.data;
  },

  /**
   * Transition election status
   */
  transitionStatus: async (
    electionId: string,
    status: 'ACTIVE' | 'CLOSING' | 'CLOSED'
  ): Promise<Election> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: Election;
    }>(`/elections/${electionId}/transition`, { status });
    return response.data;
  },

  /**
   * Force tally votes and close election
   */
  tallyElection: async (electionId: string): Promise<any> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: any;
    }>(`/elections/${electionId}/tally`);
    return response.data;
  },

  /**
   * List elections
   */
  listElections: async (params?: {
    status?: 'UPCOMING' | 'ACTIVE' | 'CLOSING' | 'CLOSED';
    includeUpcoming?: boolean;
    includeActive?: boolean;
    includeClosed?: boolean;
  }): Promise<Election[]> => {
    const response = await apiClient.get<{
      success: boolean;
      data: Election[];
    }>('/elections', { params });
    return response.data;
  },

  /**
   * Get election by ID
   */
  getElection: async (electionId: string): Promise<Election> => {
    const response = await apiClient.get<{
      success: boolean;
      data: Election;
    }>(`/elections/${electionId}`);
    return response.data;
  },

  /**
   * Get election statistics
   */
  getElectionStatistics: async (electionId: string) => {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        totalVotesCast: number;
        eligibleVoters: number;
        participationRate: number;
        status: string;
      };
    }>(`/elections/${electionId}/statistics`);
    return response.data;
  },
};

// ============================================================================
// AUDIT LOGS API
// ============================================================================

export interface AuditLog {
  id: string;
  actor_type: string;
  actor_id: string;
  action_type: string;
  resource_type: string;
  resource_id: string | null;
  election_id: string | null;
  after_state: any;
  status: string;
  timestamp: string;
  administrator?: {
    username: string;
    role: string;
  };
  voter?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface AuditLogFilters {
  actorType?: string;
  actionType?: string;
  resourceType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogStatistics {
  totalLogs: number;
  logsByType: Array<{ actionType: string; count: number }>;
  logsByActor: Array<{ actorType: string; count: number }>;
  logsByStatus: Array<{ status: string; count: number }>;
  recentActivity: number;
  previousDayActivity: number;
  uniqueActors: number;
}

export const adminAuditLogApi = {
  /**
   * Get audit logs with filters
   */
  getAuditLogs: async (filters?: AuditLogFilters) => {
    const params = new URLSearchParams();

    if (filters?.actorType) params.append('actorType', filters.actorType);
    if (filters?.actionType) params.append('actionType', filters.actionType);
    if (filters?.resourceType) params.append('resourceType', filters.resourceType);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const url = `/admin/audit-logs${queryString ? `?${queryString}` : ''}`;

    const response = await apiClient.get<{
      success: boolean;
      data: {
        logs: AuditLog[];
        total: number;
        limit: number;
        offset: number;
      };
    }>(url);

    return response.data;
  },

  /**
   * Get audit log statistics
   */
  getStatistics: async () => {
    const response = await apiClient.get<{
      success: boolean;
      data: AuditLogStatistics;
    }>('/admin/audit-logs/statistics');
    return response.data;
  },
};

// ============================================================================
// BLOCKCHAIN STATISTICS API
// ============================================================================

export interface BlockchainStatistics {
  totalTransactions: number;
  transactionsByElection: Array<{
    electionId: string;
    electionName: string;
    count: number;
    lastBlock: string;
  }>;
  recentBlocks: Array<{
    blockNumber: string;
    timestamp: Date;
    voteCount: number;
  }>;
  validationStats: Array<{
    status: string;
    count: number;
  }>;
}

export const adminBlockchainApi = {
  /**
   * Get blockchain statistics
   */
  getStatistics: async (): Promise<BlockchainStatistics> => {
    const response = await apiClient.get<{
      success: boolean;
      data: BlockchainStatistics;
    }>('/admin/blockchain/statistics');
    return response.data;
  },
};

// ============================================================================
// ERROR HANDLING
// ============================================================================

export interface ApiError {
  error: string;
  message: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

export function handleAdminApiError(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as ApiError).message;
  }
  return 'Error desconocido';
}
